import {
  PaletteOptions,
  SimplePaletteColorOptions,
  TypographyVariantsOptions,
  createTheme,
} from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
//  themeAugmentation import necessary for overriding data grid styles per https://mui.com/x/react-data-grid/getting-started/#typescript
// @ts-ignore
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import { grey } from '@mui/material/colors';

// MUI X declares these itself, but only in the themeAugmentation module the
// import below pulls in under a @ts-ignore, so they never reach the checker.
type DataGridPalette = { bg?: string; headerBg?: string; pinnedBg?: string };

/* eslint-disable @typescript-eslint/consistent-type-definitions --
   declaration merging only works on interfaces */
declare module '@mui/material/styles' {
  interface Palette {
    DataGrid: DataGridPalette;
  }
  interface PaletteOptions {
    DataGrid?: DataGridPalette;
  }
}
/* eslint-enable @typescript-eslint/consistent-type-definitions */

// rgba alpha (last parameter) corresponds to 7th and 8th symbol in hex color code
// let's assume the following were actual colors from our design system
// rgba(1, 1, 1, 0.85) = #010101d9
// rgba(12, 34, 56, 0.85) = #0c2238d9
// therefore, we'd add new enum attribute 'level0.85' = 'd9',
export enum AlphaToHexOpacity {
  'level0.08' = '14',
  'level0.26' = '42',
}

const lightPalette: PaletteOptions = {
  // rgba(0, 0, 0, 0.06)
  divider: '#0000000f',
  background: {
    default: '#F9F4EF',
    paper: '#FFFFFF',
  },
  common: {
    white: '#ffffff',
    black: '#000000',
  },
  text: {
    primary: '#082042',
    secondary: '#626262',
    disabled: '#808080',
  },
  // MUI X reads these into the --DataGrid-t-* background tokens every part of
  // the grid paints from. `pinnedBg` defaults to `background.paper` already.
  DataGrid: {
    headerBg: grey['50'],
  },
  primary: {
    main: '#E49B3D',
    dark: '#DB8A21',
    light: '#FFC85C',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#082042',
    dark: '#031732',
    light: '#6A7A8E',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#CD5437',
    dark: '#B50808',
    light: '#F5DDD7',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#E49B3D',
    dark: '#8F5300',
    light: '#FFE0A5',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#135D95',
    dark: '#063F6A',
    light: '#89C9FB',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#5EB273',
    dark: '#2F5B3A',
    light: '#D1F6C7',
    contrastText: '#FFFFFF',
  },
  action: {
    // rgba(0, 0, 0, 0.54)
    active: '#0000008a',
    // rgba(0, 0, 0, 0.04)
    hover: '#0000000a',
    // rgba(0, 0, 0, 0.08)
    selected: '#00000014',
    // rgba(0, 0, 0, 0.26)
    disabled: '#00000042',
    //rgba(0, 0, 0, 0.12);
    disabledBackground: '#0000001f',
    // rgba(0, 0, 0, 0.12)
    focus: '#0000001f',
  },
};

const darkPalette = {
  // rgba(255, 255, 255, 0.12)
  divider: '#ffffff1f',
  background: {
    default: '#F9F4EF',
    paper: '#121212',
  },
  common: {
    white: '#ffffff',
    black: '#000000',
  },
  text: {
    primary: '#FFFFFF',
    // rgba(255, 255, 255, 0.7)
    secondary: '#ffffffb3',
    // rgba(255, 255, 255, 0.5)
    disabled: '#ffffff80',
  },
  primary: {
    main: '#E49B3D',
    dark: '#DB8A21',
    light: '#FFC85C',
    // rgba(0, 0, 0, 0.87)
    contrastText: '#000000de',
  },
  secondary: {
    main: '#89C9FB',
    dark: '#2071AE',
    light: '#CFEEFF',
    // rgba(0, 0, 0, 0.87)
    contrastText: '#000000de',
  },
  error: {
    main: '#F44336',
    dark: '#B50808',
    light: '#F5DDD7',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#E49B3D',
    dark: '#8F5300',
    light: '#FFE0A5',
    // rgba(0, 0, 0, 0.87)
    contrastText: '#000000de',
  },
  info: {
    main: '#135D95',
    dark: '#063F6A',
    light: '#89C9FB',
    contrastText: '#000000de',
  },
  success: {
    main: '#5EB273',
    dark: '#2F5B3A',
    light: '#D1F6C7',
    // rgba(0, 0, 0, 0.87)
    contrastText: '#000000de',
  },
  action: {
    // rgba(255, 255, 255, 0.56)
    active: '#ffffff8f',
    // rgba(255, 255, 255, 0.08)
    hover: '#ffffff14',
    // rgba(255, 255, 255, 0.16)
    selected: '#ffffff29',
    // rgba(255, 255, 255, 0.3)
    disabled: '#ffffff4d',
    // rgba(255, 255, 255, 0.12)
    disabledBackground: '#ffffff1f',
    // rgba(255, 255, 255, 0.12)
    focus: '#ffffff1f',
  },
};

const typography: TypographyVariantsOptions = {
  fontFamily: 'Theinhardt, Arial, sans-serif;',
  h1: {
    fontWeight: 400,
    fontSize: '6rem',
    letterSpacing: '-0.094rem',
    fontFamily: 'Media77',
  },
  h2: {
    fontWeight: 400,
    fontSize: '3.75rem',
    letterSpacing: '-0.031rem',
    fontFamily: 'Media77',
    lineHeight: '4.5rem',
  },
  h3: {
    fontWeight: 400,
    fontSize: '3rem',
    fontFamily: 'Media77',
  },
  h4: {
    fontWeight: 400,
    fontSize: '2.125rem',
    fontFamily: 'Media77',
    letterSpacing: '0.016rem',
    '@media (max-width:600px)': {
      fontSize: '1.5rem',
    },
  },
  h5: {
    fontWeight: 500,
    fontSize: '1.25rem',
    fontFamily: 'Theinhardt, sans-serif',
    letterSpacing: '0.009rem',
    lineHeight: '2rem',
  },
  h6: {
    fontWeight: 500,
    fontSize: '1.25rem',
    fontFamily: 'Theinhardt, sans-serif',
    letterSpacing: '0.009rem',
    lineHeight: '2rem',
  },
  subtitle1: {
    fontWeight: 400,
    fontSize: '1rem',
    fontFamily: 'Theinhardt, sans-serif',
    letterSpacing: '0.009rem',
    lineHeight: '1.75rem',
  },
  subtitle2: {
    fontWeight: 500,
    fontSize: '0.875rem',
    fontFamily: 'Theinhardt, sans-serif',
    letterSpacing: '0.006rem',
  },
  body1: {
    fontWeight: 400,
    fontSize: '1rem',
    fontFamily: 'Theinhardt, sans-serif',
    letterSpacing: '0.009rem',
    lineHeight: '1.5rem',
  },
  body2: {
    fontWeight: 400,
    fontSize: '0.875rem',
    fontFamily: 'Theinhardt, sans-serif',
    letterSpacing: '0.009rem',
  },
  button: {
    fontWeight: 500,
    fontSize: '0.875rem',
    fontFamily: 'Theinhardt, sans-serif',
    letterSpacing: '0.125rem',
    textTransform: 'uppercase',
  },
  caption: {
    fontWeight: 400,
    fontSize: '0.875rem',
    fontFamily: 'Theinhardt, sans-serif',
    letterSpacing: '0.011rem',
  },
  overline: {
    fontWeight: 400,
    fontSize: '0.875rem',
    fontFamily: 'Theinhardt, sans-serif',
    letterSpacing: '0.011rem',
    textTransform: 'uppercase',
  },
};

const mode: PaletteMode = 'light';

const getPalette = (): PaletteOptions => {
  return mode === 'light' ? lightPalette : darkPalette;
};

const rowHoverColor = (lightPalette.primary as SimplePaletteColorOptions).main;

const theme = createTheme({
  typography,
  palette: {
    mode: 'light',
    ...(mode === 'light'
      ? // light mode
        lightPalette
      : // dark mode
        darkPalette),
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        size: 'large',
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: '#F5F5F5',
          '&.MuiLinearProgress-colorPrimary > .MuiLinearProgress-bar': {
            backgroundColor: '#082042',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          position: 'relative',
          boxShadow: '0px 0px 5px #ddd;',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          ':last-child': {
            paddingBottom: 16,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          '&.MuiAlert-filled.MuiAlert-colorInfo': {
            backgroundColor: '#135D95',
          },
          '& .MuiAlertTitle-root': {
            fontWeight: 600,
          },
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        disableRowSelectionOnClick: true,
        disableColumnMenu: true,
        disableColumnSelector: true,
      },
      styleOverrides: {
        root: {
          // v9 mixes every hover state from these two tokens. Doubled selector:
          // the grid's own defaults ship in a <body> <style> that outranks us.
          '&.MuiDataGrid-root': {
            '--DataGrid-t-color-interactive-hover': rowHoverColor,
            '--DataGrid-t-color-interactive-hover-opacity': 0.08,
          },
          '& .MuiDataGrid-virtualScroller': {
            minHeight: 250,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            color: getPalette().text?.primary,
            fontWeight: 600,
          },
          // The separator is the resize handle, so it stays in the DOM and only
          // its glyph is hidden — revealed on header hover (and while dragging)
          // so widening a column is discoverable without adding visual noise.
          '& .MuiDataGrid-columnSeparator': {
            color: 'transparent',
            transition: 'color 120ms ease-in-out',
          },
          // MUI zeroes the separator's opacity on focused, sibling-focused and
          // bordered headers, so color alone leaves the handle invisible on the
          // header the user just clicked — set both.
          '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator--resizable, & .MuiDataGrid-columnSeparator--resizing':
            {
              color: grey['400'],
              opacity: 1,
            },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus-within, .MuiDataGrid-cell:focus-within':
            {
              outline: 'none',
            },
          '& .MuiTablePagination-selectLabel': {
            color: getPalette().text?.secondary,
            ...typography.caption,
          },
          '& .MuiDataGrid-cell, .MuiDataGrid-columnHeader--sortable': {
            paddingLeft: 16,
            paddingRight: 16,
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            lineHeight: 'normal',
          },
          '& .MuiDataGrid-columnHeaderTitleContainerContent': {
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          },
          '&.task-grid': {
            border: 'none',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            '.completedRow': {
              backgroundColor: `${grey[100]} !important`,
            },
            '.blocked': {
              color: getPalette().text?.disabled,
            },
            '.regularRow': {
              backgroundColor: `${getPalette().common?.white} !important`,
            },
            '.MuiDataGrid-row:last-child': {
              borderBottomLeftRadius: 8,
              borderBottomRightRadius: 8,
            },
            // Drops the tint from row and pinned cells together; cancelling it
            // on the row alone leaves the pinned cells lit.
            '--DataGrid-t-color-interactive-hover-opacity': 0,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#031732',
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            opacity: 0.5,
          },
        },
      },
    },
  },
});

export default theme;
